import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useWorld } from "@/context/WorldContext";
import { api, fileUrl } from "@/lib/api";
import { CATEGORIES, HERO_IMG } from "@/lib/constants";
import { CategoryBadge } from "@/components/CategoryBadge";
import { OriginBadge } from "@/components/Origin";
import { WorldFormDialog } from "@/components/WorldFormDialog";

export default function WorldOverview() {
  const { world, pages, basePath, readOnly, reload } = useWorld();
  const [edit, setEdit] = useState(false);
  const nav = useNavigate();
  const recent = [...pages].sort((a, b) => b.updated_at.localeCompare(a.updated_at)).slice(0, 6);

  const remove = async () => {
    await api.delete(`/worlds/${world.id}`);
    toast.success("World unmade");
    nav("/worlds");
  };

  return (
    <div data-testid="world-overview">
      <section className="relative h-72 md:h-80 overflow-hidden">
        <img src={world.cover_image ? fileUrl(world.cover_image) : HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-canvas via-canvas/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 px-6 md:px-12 pb-8 rise">
          <p className="font-code text-xs uppercase tracking-[0.3em] text-amber-500/90">
            {world.kind === "fanfiction" ? `Fanfiction · ${world.source_material || "Unknown fandom"}` : "Original work"}{world.genre ? ` · ${world.genre}` : ""}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-amber-50 mt-2" data-testid="world-name">{world.name}</h1>
          {!readOnly && world.my_role === "owner" && (
            <div className="flex gap-2 mt-4">
              <button onClick={() => setEdit(true)} className="inline-flex items-center gap-1.5 text-xs rounded-full border border-line px-3 py-1.5 text-slate-300 hover:border-amber-500/40 transition-colors bg-panel/80 backdrop-blur-sm" data-testid="edit-world-btn">
                <Pencil className="w-3 h-3" /> Edit world
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="inline-flex items-center gap-1.5 text-xs rounded-full border border-line px-3 py-1.5 text-slate-300 hover:border-rose-500/50 hover:text-rose-300 transition-colors bg-panel/80 backdrop-blur-sm" data-testid="delete-world-btn">
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-panel border-line">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="font-display text-2xl">Unmake {world.name}?</AlertDialogTitle>
                    <AlertDialogDescription>All pages, events and relationships will be lost forever.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-world-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={remove} className="bg-rose-600 hover:bg-rose-500" data-testid="delete-world-confirm">Delete world</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>
      </section>
      <div className="px-6 md:px-12 py-10 max-w-5xl space-y-12">
        {world.description && <p className="font-display text-xl md:text-2xl italic text-slate-300 leading-relaxed max-w-3xl" data-testid="world-description">{world.description}</p>}
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const n = pages.filter((p) => p.category === c.key).length;
            return (
              <Link key={c.key} to={`${basePath}/c/${c.key.toLowerCase()}`} style={{ "--glow": c.hex }}
                className={`rounded-xl border ${c.border} ${c.bg} p-4 transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-[0_0_24px_-8px_var(--glow)]`} data-testid={`stat-${c.key.toLowerCase()}`}>
                <Icon className={`w-4 h-4 ${c.text}`} />
                <p className="font-display text-3xl text-slate-100 mt-2">{n}</p>
                <p className="text-xs text-slate-400">{c.key}</p>
              </Link>
            );
          })}
        </div>
        <section>
          <h2 className="font-display text-2xl text-amber-200/90 mb-4">Recently inscribed</h2>
          {recent.length === 0 ? (
            <p className="text-slate-500 italic">No pages yet. {!readOnly && <Link to={`${basePath}/new`} className="text-amber-400 not-italic hover:underline" data-testid="first-page-link">Write the first one.</Link>}</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              {recent.map((p) => (
                <Link key={p.id} to={`${basePath}/p/${p.id}`} data-testid={`recent-page-${p.id}`}
                  className="flex gap-4 rounded-xl border border-line bg-panel p-4 hover:border-amber-500/40 transition-colors">
                  {p.image && <img src={fileUrl(p.image)} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
                  <div className="min-w-0">
                    <CategoryBadge category={p.category} /> <OriginBadge page={p} />
                    <p className="font-display text-xl text-slate-100 mt-1 truncate">{p.title}</p>
                    <p className="text-xs text-slate-500 line-clamp-1">{p.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </section>
      </div>
      {!readOnly && <WorldFormDialog open={edit} onOpenChange={setEdit} initial={world} onSaved={reload} />}
    </div>
  );
}
